import React from 'react';
import { css } from '@emotion/react';
import { useTranslation } from '@frontend/i18n';
import { theme } from '@frontend/theme';
import { AlertIcon } from '@frontend/design-system';
import { Chip, Text, Truncated } from '@frontend/design-system';

export type SectionInfoProps = {
  label: string;
  value: string;
  mismatchRequestValue?: string;
};

export const SectionInfo = React.memo(({ label, value, mismatchRequestValue }: SectionInfoProps) => {
  const { t } = useTranslation('insurance-eligibility-report');
  const hasMismatch = !!mismatchRequestValue;
  return (
    <section>
      <Text size='small' color='subdued'>
        <Truncated>{label}</Truncated>
      </Text>
      {hasMismatch ? (
        <>
          <div css={mismatchValueRowStyles}>
            <Text weight='bold' css={mismatchValueStyles}>
              <Truncated>{!!value ? value : '-'}</Truncated>
            </Text>
            <Chip
              css={{ backgroundColor: `${theme.colors.warning30}40`, color: theme.colorsV2.yellow70() }}
              size='tiny'
              leftElement={<AlertIcon size={12} css={{ color: theme.colorsV2.yellow70() }} />}
            >
              {t('Differs')}
            </Chip>
          </div>
          <Text size='small' css={requestedValueStyles}>
            <Truncated>
              {t('Requested')}: {mismatchRequestValue}
            </Truncated>
          </Text>
        </>
      ) : (
        <Text weight='bold'>
          <Truncated>{!!value ? value : '-'}</Truncated>
        </Text>
      )}
    </section>
  );
});

SectionInfo.displayName = 'SectionInfo';

const mismatchValueRowStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(0.5)};
`;

const mismatchValueStyles = css`
  min-width: 0;
  flex: 1;
`;

const requestedValueStyles = css`
  color: ${theme.colors.warning70};
`;
